import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { RADIO_PLAN_DESC } from '@app/core/models/Radio';
import { ShoutOut } from '@app/core/models/ShoutOut';
import { Track } from '@app/core/models/Track';
import { RadioWatcherService } from '@app/core/ngxs/radio-watcher.service';
import { DataResolved } from '@app/core/resolvers/generic-resolver.resolver';
import { LiveTrackingState } from '@app/core/states/live-tracking.state';
import { RadioState } from '@app/core/states/radio.state';
import { EnableShoutOutRequest } from '@app/core/states/shout-outs.actions';
import { ShoutOutsState } from '@app/core/states/shout-outs.state';
import { getBacTypeAccordingToName } from '@app/library/components/library-utils';
import { Bac } from '@app/library/models/bac.model';
import { SearchService } from '@app/library/services/search.service';
import {
  ResetFilterForBacCurrentlyEditing,
  SetFilterForBacCurrentlyEditing,
} from '@app/library/states/filters.actions';
import { FiltersState } from '@app/library/states/filters.state';
import { SetMobilePlaylistColumns } from '@app/library/states/playlist-columns.actions';
import { UnselectAll } from '@app/library/states/selected-tracks.actions';
import {
  AddDefaultTracksFailure,
  AddDefaultTracksRequest,
  AddDefaultTracksSuccess,
  TracksFailure,
  TracksRequest,
  TracksSuccess,
  TrackTagsSaveSuccess,
  UpdateTrackFilter,
  UpdateTracksFilter,
} from '@app/library/states/tracks.actions';
import { TracksState } from '@app/library/states/tracks.state';
import { UpgradeOfferModalComponent } from '@app/shared/modals/upgrade-offer/components/upgrade-offer-modal/upgrade-offer-modal.component';
import { planBigger } from '@app/shared/utils';
import { Actions, ofActionSuccessful, Select, Store } from '@ngxs/store';
import { UiStoreHelperService } from '@radioking/shared/common-services';
import { isArray } from 'lodash';
import moment from 'moment-timezone';
import { BehaviorSubject, fromEvent, Observable, Subscription } from 'rxjs';
import {
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  mapTo,
  shareReplay,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

import { distinctUntilChangedWithSideEffectOnLike } from '../../../../core/rxjs/rxOperators';
import { ColumnShown } from '../../../models/column-shown.model';
import {
  ColumnTypes,
  SetMobileColumns,
  ToggleColumn,
} from '../../../states/columns.actions';
import { ColumnsState } from '../../../states/columns.state';
import { LibraryHeaderComponent } from '../library-header/library-header.component';

@Component({
  selector: 'rk-library-all',
  templateUrl: './library-all.component.html',
  styleUrls: ['./library-all.component.scss'],
})
export class LibraryAllComponent implements OnInit, OnDestroy {
  currentBac$: Observable<Bac>;

  @Select(ColumnsState.getColumnShow)
  columns$: Observable<ColumnShown>;

  @Select(RadioState.currentRadioId)
  myRadioId$: Observable<number>;

  @Select(ShoutOutsState.shoutOuts)
  shoutOuts$: Observable<ShoutOut>;

  @Select(LiveTrackingState.planDesc)
  planDesc$: Observable<RADIO_PLAN_DESC>;

  @ViewChild('bacHeader')
  header: LibraryHeaderComponent;

  tracks$: Observable<Track[]>;

  isCurrentBacWithFilters$: Observable<boolean>;

  tracksUnfiltered$: Observable<Track[]>;

  tracksTime$: Observable<number>;

  isLoading$: Observable<boolean>;

  isImportingDefaultTracks$: Observable<boolean>;

  searchSubject = new BehaviorSubject<string>('');

  subsriptions = new Subscription();

  lastChangeOfTracks: moment.Moment | null = null;

  constructor(
    private readonly store: Store,
    private readonly route: ActivatedRoute,
    private readonly searchService: SearchService,
    private readonly watcher: RadioWatcherService,
    private readonly helper: UiStoreHelperService,
    private readonly actions$: Actions,
    private readonly router: Router,
    private readonly dialog: MatDialog,
  ) {}

  ngOnInit() {
    this.setMobileColumns(window.innerWidth);
    this.subsriptions.add(
      fromEvent(window, 'resize')
        .pipe(debounceTime(100))
        .subscribe(w => {
          if (w && w.target) {
            this.setMobileColumns((w.target as Window).innerWidth);
          }
        }),
    );

    this.currentBac$ = this.route.data.pipe(
      map<any, DataResolved<Bac>>(val => val.bacs),
      tap(() => this.clearSearch()),
      switchMap(val => val.data$),
      filter(val => typeof val !== 'undefined'),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.isCurrentBacWithFilters$ = this.currentBac$.pipe(
      switchMap(bac =>
        this.store.select(FiltersState.hasBacAnyFilter).pipe(map(fn => fn(bac.id))),
      ),
      startWith(false),
    );

    this.isLoading$ = this.helper.getLoadingState(
      TracksRequest,
      [TracksFailure, TracksSuccess],
      true,
    );

    this.isImportingDefaultTracks$ = this.helper.getLoadingState(
      AddDefaultTracksRequest,
      [AddDefaultTracksSuccess, AddDefaultTracksFailure],
    );

    const bacRequestSubscription = this.currentBac$
      .pipe(
        distinctUntilChanged(),
        switchMap(bac =>
          this.watcher.dispatchAndWatchAsObservable(
            new TracksRequest(bac),
            this.actions$.pipe(
              ofActionSuccessful(
                ResetFilterForBacCurrentlyEditing,
                SetFilterForBacCurrentlyEditing,
              ),
            ),
          ),
        ),
      )
      .subscribe();

    this.subsriptions.add(bacRequestSubscription);

    const watcherSubscription = this.actions$
      .pipe(
        ofActionSuccessful(UpdateTrackFilter, UpdateTracksFilter, TrackTagsSaveSuccess),
        switchMap(() => this.currentBac$),
        switchMap(bac =>
          this.store.selectOnce(FiltersState.hasBacAnyFilter).pipe(
            map(fn => fn(bac.id)),
            filter(value => !!value),
            mapTo(bac),
          ),
        ),
      )
      .subscribe(bac => {
        this.store.dispatch(new TracksRequest(bac));
      });
    this.subsriptions.add(watcherSubscription);

    const currentBacName$ = this.currentBac$.pipe(
      map(bac => getBacTypeAccordingToName(bac.name)),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.tracksUnfiltered$ = currentBacName$.pipe(
      switchMap(name =>
        this.store.select(TracksState.tracksBac).pipe(map(fn => fn(name))),
      ),
      distinctUntilChanged(),
      shareReplay({ refCount: true, bufferSize: 1 }),
    );

    this.tracksTime$ = currentBacName$.pipe(
      switchMap(name =>
        this.store.select(TracksState.tracksBacTime).pipe(map(fn => fn(name))),
      ),
    );

    this.tracks$ = this.searchService
      .createTracksSearch(this.tracksUnfiltered$, this.searchSubject.asObservable())
      .pipe(
        filter(val => isArray(val)),
        distinctUntilChangedWithSideEffectOnLike(tracks => {
          if (this.shouldUnselectAllWhenTracksChanged(tracks)) {
            this.store.dispatch(new UnselectAll());
          }
          this.lastChangeOfTracks = moment();
        }),
        shareReplay({ refCount: true, bufferSize: 1 }),
      );

    this.store.dispatch(new UnselectAll());
    this.subsriptions.add(
      this.router.events.subscribe(e => {
        if (e instanceof NavigationEnd) {
          this.store.dispatch(new UnselectAll());
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subsriptions.unsubscribe();
  }

  setMobileColumns(w: number) {
    if (w < 1081) {
      this.store.dispatch(new SetMobileColumns());
      this.store.dispatch(new SetMobilePlaylistColumns());
    }
  }

  /**
   * Track selection should be emptied when some tracks appear or disappear in list,
   * except when tracks appear from upload (new tracks).
   */
  shouldUnselectAllWhenTracksChanged(tracks: Track[]) {
    return (
      !this.lastChangeOfTracks ||
      !tracks.some(
        ({ addedToLibraryDate }) => addedToLibraryDate?.isAfter(this.lastChangeOfTracks),
      )
    );
  }

  onColumnChange(column: ColumnTypes): void {
    this.store.dispatch(new ToggleColumn(column));
  }

  onChangeSearch(str: string) {
    if (typeof str === 'string') {
      this.searchSubject.next(str);
    }
  }

  clearSearch() {
    if (this.header) {
      this.header.clearSearchBar();
    }
    this.searchSubject.next('');
  }

  isBacShoutsOut(bac: Bac) {
    return getBacTypeAccordingToName(bac.name) === 'dedication';
  }

  isBacChronic(bac: Bac) {
    return getBacTypeAccordingToName(bac.name) === 'chronic';
  }

  updateOffer(): void {
    this.dialog.open(UpgradeOfferModalComponent, { data: 'library_audio_shouts_plan' });
  }

  enableShoutOuts() {
    this.store.dispatch(new EnableShoutOutRequest());
  }

  planBigger(plan: RADIO_PLAN_DESC, compareTo: RADIO_PLAN_DESC) {
    return planBigger(plan, compareTo);
  }
}
